/**
 * PropertyFormFields — shared form fields for creating/editing a CRM property.
 *
 * Renders the standard property configuration fields used across the app:
 * Label, Description, Field Type, Select Options (conditional), and Unique
 * checkbox. This component is purely presentational — it renders fields and
 * calls back on change, with no API submission or validation framework.
 *
 * Used by:
 *   - NewPropertiesPanel (CSV import mapping step)
 *   - (future) PropertyTypeSlideout, InlinePropertyEditor
 */

import { useCallback } from 'react';

import { Checkbox } from '@/lib/base/forms/checkbox';
import { Input } from '@/lib/base/forms/input/input';
import { Select } from '@/lib/base/forms/select/select';
import { TextArea } from '@/lib/base/forms/textarea/textarea';
import type { getFieldTypeItems } from '@/lib/crm/utils/fieldTypeHelpers';
import { SelectOptionsEditor } from '@/pages/dashboard/profile/components/SelectOptionsEditor';

import type { SelectOption } from './types';

// =============================================================================
// Types
// =============================================================================

export interface PropertyFormValues {
  label: string;
  description: string;
  fieldType: string;
  options: SelectOption[];
  isUnique: boolean;
}

export interface PropertyFormFieldsProps {
  /** Current form values */
  values: PropertyFormValues;
  /** Called when any field changes — receives the full updated values object */
  onChange: (values: PropertyFormValues) => void;
  /** Pre-computed field type items from useFieldTypes + getFieldTypeItems */
  fieldTypeItems: ReturnType<typeof getFieldTypeItems>;
  /** Whether field types are still loading */
  fieldTypesLoading?: boolean;
  /** Whether all fields should be disabled */
  isDisabled?: boolean;
  /** Whether the label field is editable (defaults to true) */
  isLabelEditable?: boolean;
  /** Whether to render the Description field (defaults to true) */
  showDescription?: boolean;
  /** Whether to render the "Require unique values" field (defaults to true) */
  showUnique?: boolean;
}

// =============================================================================
// Component
// =============================================================================

export function PropertyFormFields({
  values,
  onChange,
  fieldTypeItems,
  fieldTypesLoading = false,
  isDisabled = false,
  isLabelEditable = true,
  showDescription = true,
  showUnique = true,
}: PropertyFormFieldsProps) {
  const handleLabelChange = useCallback(
    (newLabel: string) => {
      onChange({ ...values, label: newLabel });
    },
    [values, onChange]
  );

  const handleDescriptionChange = useCallback(
    (newDescription: string) => {
      onChange({ ...values, description: newDescription });
    },
    [values, onChange]
  );

  const handleFieldTypeChange = useCallback(
    (key: string | number | null) => {
      const newType = key as string;
      const updates: Partial<PropertyFormValues> = { fieldType: newType };
      // Clear options when moving away from select
      if (newType !== 'select') {
        updates.options = [];
      }
      onChange({ ...values, ...updates });
    },
    [values, onChange]
  );

  const handleOptionsChange = useCallback(
    (newOptions: SelectOption[]) => {
      onChange({ ...values, options: newOptions });
    },
    [values, onChange]
  );

  const handleIsUniqueChange = useCallback(
    (selected: boolean) => {
      onChange({ ...values, isUnique: selected });
    },
    [values, onChange]
  );

  return (
    <div className='space-y-4'>
      {/* Label */}
      <Input
        label='Label'
        placeholder='e.g. Priority'
        value={values.label}
        onChange={handleLabelChange}
        isDisabled={isDisabled || !isLabelEditable}
        isRequired
      />

      {/* Description */}
      {showDescription && (
        <TextArea
          label='Description'
          placeholder='Optional description for this property'
          value={values.description}
          onChange={handleDescriptionChange}
          rows={2}
          resize='none'
          isDisabled={isDisabled}
        />
      )}

      {/* Field Type */}
      <Select
        label='Field type'
        placeholder={fieldTypesLoading ? 'Loading…' : 'Select field type'}
        items={fieldTypeItems}
        selectedKey={values.fieldType}
        onSelectionChange={handleFieldTypeChange}
        isRequired
        isDisabled={isDisabled || fieldTypesLoading}
      >
        {item => <Select.Item id={item.id}>{item.label}</Select.Item>}
      </Select>

      {/* Options for select type */}
      {values.fieldType === 'select' && (
        <SelectOptionsEditor
          options={values.options}
          onChange={handleOptionsChange}
          disabled={isDisabled}
        />
      )}

      {/* Unique checkbox */}
      {showUnique && (
        <Checkbox
          label='Require unique values'
          hint='Each record must have a different value for this property.'
          isSelected={values.isUnique}
          onChange={handleIsUniqueChange}
          isDisabled={isDisabled}
        />
      )}
    </div>
  );
}
